'use client';

import React from 'react';

type StackedSourceListTextProps = {
  filename: string;
  /** Breadcrumb path rendered below the filename, e.g. `Case / Folder / file.pdf`. */
  displayPath: string;
  /** Citation metadata on the same line as the filename, e.g. `(Page 308)`. */
  metadata?: string;
  className?: string;
};

/** Turn a slash path into the Figma breadcrumb readout (`a/b/c` → `a / b / c`). */
export function formatStackedSourceDisplayPath(pathLabel: string): string {
  return pathLabel
    .split('/')
    .map((segment) => segment.trim())
    .filter(Boolean)
    .join(' / ');
}

/**
 * Figma ".FileName + Metadata": filename flush left, metadata inline on row one,
 * full path immediately below on row two.
 */
export function StackedSourceListText({
  filename,
  displayPath,
  metadata,
  className,
}: StackedSourceListTextProps) {
  const normalizedPath = displayPath.trim();
  const showDisplayPath =
    normalizedPath.length > 0 &&
    normalizedPath !== filename &&
    normalizedPath !== formatStackedSourceDisplayPath(filename);

  return (
    <div
      className={`flex min-w-0 flex-col items-start justify-center overflow-hidden text-left ${className ?? ''}`}
    >
      <div className="flex min-w-0 max-w-full items-baseline gap-1">
        <span
          className="min-w-0 truncate text-[14px] font-medium leading-5 text-[color:var(--text-inverse,white)]"
          title={filename}
        >
          {filename}
        </span>
        {metadata ? (
          <span className="shrink-0 text-[12px] font-semibold leading-4 tracking-[0.36px] text-[color:var(--text-muted,#6888a6)]">
            {metadata}
          </span>
        ) : null}
      </div>
      {showDisplayPath ? (
        <span
          className="max-w-full truncate text-[12px] font-semibold leading-4 tracking-[0.36px] text-[color:var(--text-placeholder,#9ab0c4)]"
          title={normalizedPath}
        >
          {normalizedPath}
        </span>
      ) : null}
    </div>
  );
}
